/**
 * TrackCard — everything about one track, in a sheet from the bottom edge.
 *
 * Task 05, checklist items 6 and 7: «полная строка исполнителей видна в карточке трека» and
 * «теги показаны как кнопки; нажатие открывает список по тегу». The row in the list may cut a
 * long billing with an ellipsis; here it is written out whole and wraps. Beside it, three
 * ways onward — the artist's list under the FIRST name of the billing (the row of
 * «Исполнители» the track is counted under), the riddim's list, and one button per tag.
 *
 * A sheet rather than a screen: the list behind it keeps its place, and a thumb that tapped a
 * row near the bottom of the phone reaches every button on it without moving.
 *
 * ## It is a modal, and behaves like one
 *
 * `role="dialog"` with `aria-modal`, focus moved to «Закрыть» on open so a screen reader lands
 * inside it, Escape and a tap on the dimmed list both close it. Since task 71 «Закрыть» is the
 * cross in the corner, named so for a screen reader.
 *
 * ## The bill's card (task 71)
 *
 * A sheet as tall as what it holds: the cover at the left, the name large and gold beside it, the
 * artist, the year and the length; «Исполнитель · теги» — the ways onward, one row of chips that
 * wraps; «Слушать» in gold across the width; and three buttons with an icon over a word — «В
 * очередь», «Следующим», «В чат» (`docs/superpowers/specs/2026-10-25-oblik-afisha-design.md`).
 *
 * ## «В очередь» keeps the card open
 *
 * Task 08, `spec.md` story 28: «добавлять трек в очередь». The track goes to the end of the
 * queue and the card says so in a line of its own, instead of closing: closing would be the
 * only feedback, and a card that vanishes looks the same whether the tap worked or not.
 *
 * ## «Играть следующим» beside it (task 44)
 *
 * The same, but right after the track playing (`store/queue.ts`, `insertNext`): under shuffle
 * too, it plays next, and the queue sheet marks it «следующим». The card stays open and says so.
 *
 * ## «Отправить в чат» of this track (task 50)
 *
 * The share button of «Сейчас играет», for the card's track, with its words written out; a share
 * that failed says so in the card's status line. A client that cannot open the dialog draws no
 * button (`components/ShareButton.tsx`).
 */

import type { JSX } from 'react';
import { useEffect, useId, useRef, useState } from 'react';

import { player } from '../engine/player';
import type { TrackItem } from '../lib/api';
import { formatDuration } from '../lib/format';
import type { Collection } from '../store/library';

import { Cover } from './Cover';
import { CloseGlyph } from './KeyIcons';
import { NextIcon, PlayIcon, QueueIcon } from './PlayerControls';
import { SHARE_FAILED_TEXT, ShareButton } from './ShareButton';

export const CARD_ARTIST_LABEL = 'Исполнитель';
export const CARD_RIDDIM_LABEL = 'Riddim';
export const CARD_TAGS_LABEL = 'Теги';
/** Task 71: «В чат» under its icon — «Отправить в чат» is what a screen reader hears. */
export const SHARE_SHORT_LABEL = 'В чат';
export const CLOSE_LABEL = 'Закрыть';
export const LISTEN_LABEL = 'Слушать';
export const ENQUEUE_LABEL = 'В очередь';
export const ENQUEUED_TEXT = 'Добавлено в конец очереди.';
export const QUEUE_FULL_TEXT = 'Очередь заполнена: в ней уже пятьсот треков.';
/** Task 44; task 71: one word under its icon, as on the approved picture. */
export const PLAY_NEXT_LABEL = 'Следующим';
export const PLAYS_NEXT_TEXT = 'Заиграет следующим.';

export interface TrackCardProps {
  readonly track: TrackItem;
  readonly onClose: () => void;
  readonly onOpenCollection: (collection: Collection) => void;
  /** Task 07: the card plays the track too — the row is not the only way to hear it. */
  readonly onPlay: (track: TrackItem) => void;
  /** Task 08: appends the track to the queue. `false` when the queue is full. */
  readonly onEnqueue: (track: TrackItem) => boolean;
}

/** What the line under the buttons says: the last thing a button of the card did. */
type CardStatus = 'added' | 'next' | 'full' | 'share-failed';

const CARD_STATUS_TEXT: Readonly<Record<CardStatus, string>> = {
  added: ENQUEUED_TEXT,
  next: PLAYS_NEXT_TEXT,
  full: QUEUE_FULL_TEXT,
  'share-failed': SHARE_FAILED_TEXT,
};

export function TrackCard({
  track,
  onClose,
  onOpenCollection,
  onPlay,
  onEnqueue,
}: TrackCardProps): JSX.Element {
  const titleId = useId();
  const closeRef = useRef<HTMLButtonElement | null>(null);
  const [status, setStatus] = useState<CardStatus | undefined>(undefined);

  useEffect(() => {
    closeRef.current?.focus();
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  const meta = [track.year === null ? '' : String(track.year), formatDuration(track.durationS)]
    .filter((part) => part !== '')
    .join(' · ');

  /** Task 71: what the row of chips holds, said over it — «Исполнитель · Riddim · теги». */
  const factsLabel = [
    track.artist === null ? '' : CARD_ARTIST_LABEL,
    track.riddim === null ? '' : CARD_RIDDIM_LABEL,
    track.tags.length === 0 ? '' : CARD_TAGS_LABEL,
  ]
    .filter((part) => part !== '')
    .map((part, at) => (at === 0 ? part : part === CARD_RIDDIM_LABEL ? part : part.toLowerCase()))
    .join(' · ');

  return (
    <>
      <div className="sheetBackdrop" aria-hidden="true" onClick={onClose} />
      <section
        className="sheet cardSheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <div className="sheetGrab" aria-hidden="true" />
        <button
          ref={closeRef}
          type="button"
          className="sheetClose"
          aria-label={CLOSE_LABEL}
          onClick={onClose}
        >
          <CloseGlyph className="keyIcon" />
        </button>

        <div className="cardHead">
          <Cover src={track.cover} className="cardCover" />
          <div className="cardText">
            <h2 id={titleId} className="cardTitle">
              {track.title}
            </h2>
            {track.performer !== '' && <p className="cardPerformer">{track.performer}</p>}
            {meta !== '' && <p className="cardMeta">{meta}</p>}
          </div>
        </div>

        {factsLabel !== '' && (
          <div className="cardFacts">
            <p className="cardFactLabel">{factsLabel}</p>
            <div className="chips">
              {track.artist !== null && (
                <button
                  type="button"
                  className="chip"
                  onClick={() => {
                    if (track.artist === null) return;
                    onOpenCollection({
                      kind: 'artist',
                      key: track.artist.key,
                      name: track.artist.name,
                    });
                  }}
                >
                  {track.artist.name}
                </button>
              )}
              {track.riddim !== null && (
                <button
                  type="button"
                  className="chip chipGold"
                  onClick={() => {
                    if (track.riddim === null) return;
                    onOpenCollection({ kind: 'riddim', ...track.riddim });
                  }}
                >
                  {track.riddim.name}
                </button>
              )}
              {track.tags.map((tag) => (
                <button
                  key={tag.id}
                  type="button"
                  className="chip"
                  onClick={() => {
                    onOpenCollection({ kind: 'tag', ...tag });
                  }}
                >
                  #{tag.name}
                </button>
              ))}
            </div>
          </div>
        )}

        <button
          type="button"
          className="button cardListen"
          onClick={() => {
            onPlay(track);
            onClose();
          }}
        >
          <PlayIcon className="keyIcon" />
          {LISTEN_LABEL}
        </button>
        <div className="cardKeys">
          <button
            type="button"
            className="key cardKey"
            onClick={() => {
              setStatus(onEnqueue(track) ? 'added' : 'full');
            }}
          >
            <QueueIcon className="keyIcon" />
            <span>{ENQUEUE_LABEL}</span>
          </button>
          <button
            type="button"
            className="key cardKey"
            onClick={() => {
              // Task 44: one player for the whole app, like the share below — no prop to pass it.
              setStatus(player().playNext(track) ? 'next' : 'full');
            }}
          >
            <NextIcon className="keyIcon" />
            <span>{PLAY_NEXT_LABEL}</span>
          </button>
          <ShareButton
            trackId={track.id}
            onFailedChange={(failed) => {
              setStatus(failed ? 'share-failed' : undefined);
            }}
            className="key cardKey"
            iconClassName="keyIcon"
            withLabel
            shortLabel={SHARE_SHORT_LABEL}
          />
        </div>
        <p className="cardStatus" role="status">
          {status === undefined ? '' : CARD_STATUS_TEXT[status]}
        </p>
      </section>
    </>
  );
}
